/**
 * @file appTableTypes.ts
 * @description Type definitions for the AppTable system.
 * 
 * GOVERNED BY: copilot-instructions.md – Sections 7, 23 (UI Standards)
 * 
 * Features:
 * - Config-driven column definitions
 * - Generic type support for any data shape
 * - Sorting, pagination, and selection support
 * - No hardcoding enforcement
 */

import type { ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────
// Column Configuration
// ─────────────────────────────────────────────────────────────

/**
 * Column alignment options.
 */
export type ColumnAlign = 'left' | 'center' | 'right';

/**
 * Column definition for AppTable.
 * 
 * @example
 * ```tsx
 * const columns: AppTableColumn<User>[] = [
 *   { key: 'name', header: 'Name', width: '25%' },
 *   { key: 'email', header: 'Email', width: '30%', truncate: true },
 *   { key: 'actions', header: 'Actions', width: '120px', align: 'right', render: (row) => <ActionCell /> }
 * ]
 * ```
 */
export interface AppTableColumn<TRow> {
  /** Unique key for the column (can be keyof TRow or custom string) */
  key: keyof TRow | string;
  
  /** Column header text */
  header: string;
  
  /** Column width (CSS value: '25%', '120px', '1fr', etc.) */
  width?: string;
  
  /** Minimum column width */
  minWidth?: string;
  
  /** Maximum column width */
  maxWidth?: string;
  
  /** Text alignment */
  align?: ColumnAlign;
  
  /** Whether to truncate text with ellipsis (default: true) */
  truncate?: boolean;
  
  /** Whether column is sortable (server-side) */
  sortable?: boolean;
  
  /** Whether column can be hidden via column toggle */
  hideable?: boolean;
  
  /** Whether column is visible by default */
  defaultVisible?: boolean;
  
  /** Custom cell renderer */
  render?: (row: TRow, index: number) => ReactNode;
  
  /** Custom header renderer */
  renderHeader?: () => ReactNode;
  
  /** Additional cell className */
  cellClassName?: string;
  
  /** Additional header className */
  headerClassName?: string;
  
  /** Whether column is hidden (controlled) */
  hidden?: boolean;
  
  /** Whether column is sticky */
  sticky?: 'left' | 'right';
}

// ─────────────────────────────────────────────────────────────
// Sorting
// ─────────────────────────────────────────────────────────────

/**
 * Sort direction.
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Sort state for a column.
 */
export interface SortState<TRow> {
  /** Column key being sorted */
  column: keyof TRow | string;
  /** Sort direction */
  direction: SortDirection;
}

// ─────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────

/**
 * Pagination configuration.
 */
export interface PaginationConfig {
  /** Current page (1-indexed) */
  page: number;
  /** Items per page */
  pageSize: number;
  /** Total items */
  totalItems: number;
  /** Available page sizes */
  pageSizeOptions?: number[];
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
}

// ─────────────────────────────────────────────────────────────
// Selection
// ─────────────────────────────────────────────────────────────

/**
 * Selection configuration.
 */
export interface SelectionConfig<TRow> {
  /** Selected row keys */
  selectedKeys: Set<string>;
  /** Function to get row key */
  getRowKey: (row: TRow) => string;
  /** Callback when selection changes */
  onSelectionChange?: (selectedKeys: Set<string>) => void;
  /** Whether to allow multi-select */
  multiSelect?: boolean;
  /** Whether a row is selectable */
  isRowSelectable?: (row: TRow) => boolean;
}

// ─────────────────────────────────────────────────────────────
// AppTable Props
// ─────────────────────────────────────────────────────────────

/**
 * Props for AppTable component.
 */
export interface AppTableProps<TRow> {
  /** Table data array */
  data: TRow[];
  
  /** Column definitions */
  columns: AppTableColumn<TRow>[];
  
  /** Unique key for each row (defaults to index) */
  getRowKey?: (row: TRow, index: number) => string;
  
  /** Loading state */
  loading?: boolean;
  
  /** Loading text */
  loadingText?: string;
  
  /** Empty state title */
  emptyTitle?: string;
  
  /** Empty state description */
  emptyDescription?: string;
  
  /** Empty state icon */
  emptyIcon?: ReactNode;
  
  /** Empty state action */
  emptyAction?: ReactNode;
  
  /** Error state */
  error?: string | null;
  
  /** Sort state */
  sort?: SortState<TRow>;
  
  /** Sort change callback */
  onSortChange?: (sort: SortState<TRow>) => void;
  
  /** Pagination config */
  pagination?: PaginationConfig;
  
  /** Selection config */
  selection?: SelectionConfig<TRow>;
  
  /** Column visibility state */
  visibleColumns?: VisibleColumnsState;
  
  /** Row click handler */
  onRowClick?: (row: TRow, index: number) => void;
  
  /** Whether row is clickable (visual indicator) */
  rowClickable?: boolean;
  
  /** Custom row className */
  getRowClassName?: (row: TRow, index: number) => string;
  
  /** Whether to show zebra striping */
  striped?: boolean;
  
  /** Whether to show borders between rows */
  bordered?: boolean;
  
  /** Whether table is compact */
  compact?: boolean;
  
  /** Whether to highlight row on hover */
  hoverable?: boolean;
  
  /** Table caption for accessibility */
  caption?: string;
  
  /** Additional table className */
  className?: string;
  
  /** Additional wrapper className */
  wrapperClassName?: string;
  
  /** Test ID for automation */
  testId?: string;
}

// ─────────────────────────────────────────────────────────────
// Style Constants (LOCKED)
// ─────────────────────────────────────────────────────────────

/**
 * Locked style constants for AppTable.
 * CRITICAL: These ensure consistent table appearance.
 */
export const APP_TABLE_STYLES = {
  /** Wrapper - isolate horizontal scrolling to table container only */
  wrapper: 'w-full overflow-x-auto overflow-y-hidden rounded-sm border border-border bg-card',
  
  /** Table layout - fixed desktop layout with mobile-safe minimum width */
  table: 'w-full min-w-[640px] lg:min-w-full table-fixed',
  
  /** Header row */
  headerRow: 'sticky top-0 z-10 bg-card border-b border-border',
  
  /** Header cell */
  headerCell: 'text-left px-3 py-2.5 text-[12px] font-semibold text-muted-foreground uppercase tracking-wide',
  
  /** Body row - default */
  bodyRow: 'table-row h-11 border-t border-border/50 transition-colors',
  
  /** Body row - hover */
  bodyRowHover: 'hover:bg-muted/40',
  
  /** Body row - clickable */
  bodyRowClickable: 'cursor-pointer',
  
  /** Body row - striped */
  bodyRowStriped: 'even:bg-muted/20',
  
  /** Body row - selected */
  bodyRowSelected: 'bg-primary/5',
  
  /** Body cell */
  bodyCell: 'px-3 py-2 text-[12px] text-foreground align-middle',
  
  /** Cell truncation */
  truncate: 'truncate',
  
  /** Alignment classes */
  alignLeft: 'text-left',
  alignCenter: 'text-center',
  alignRight: 'text-right',
  
  /** Compact mode adjustments */
  compactHeaderCell: 'px-2 py-2 text-[11px] align-middle',
  compactBodyCell: 'px-2 py-1.5 text-[11px] align-middle',
  
  /** Sticky column */
  stickyLeft: 'sticky left-0 bg-surface-card z-10',
  stickyRight: 'sticky right-0 bg-surface-card z-10',
} as const;

// ─────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────

/**
 * Get cell value from row using column key.
 */
export function getCellValue<TRow>(row: TRow, key: keyof TRow | string): unknown {
  if (typeof key === 'string' && key.includes('.')) {
    // Support nested keys like 'user.name'
    return key.split('.').reduce((obj: unknown, k) => {
      if (obj && typeof obj === 'object' && k in obj) {
        return (obj as Record<string, unknown>)[k];
      }
      return undefined;
    }, row);
  }
  return row[key as keyof TRow];
}

/**
 * Format cell value for display.
 */
export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleDateString();
  return String(value);
}

// ─────────────────────────────────────────────────────────────
// Server-Side Query Params
// ─────────────────────────────────────────────────────────────

/**
 * Standard query params for server-side table operations.
 * Use this consistently across all table API calls.
 */
export interface TableQueryParams {
  /** Current page (1-indexed) */
  page: number;
  /** Items per page */
  pageSize: number;
  /** Column key to sort by */
  sortBy?: string;
  /** Sort direction */
  sortDirection?: SortDirection;
  /** Search query */
  search?: string;
}

/**
 * Paginated response shape from API.
 * Follows EPPREM locked API contract.
 */
export interface PaginatedData<T> {
  /** Data items */
  items: T[];
  /** Current page */
  page: number;
  /** Page size */
  pageSize: number;
  /** Total count of items */
  totalCount: number;
  /** Total pages */
  totalPages: number;
}

/**
 * Full API response wrapper (matches locked contract).
 */
export interface PaginatedApiResponse<T> {
  success: boolean;
  data: PaginatedData<T>;
  message: string;
  errors: string[];
  correlationId: string;
}

// ─────────────────────────────────────────────────────────────
// Column Visibility
// ─────────────────────────────────────────────────────────────

/**
 * Column visibility state.
 * Maps column keys to visibility boolean.
 */
export type VisibleColumnsState = Record<string, boolean>;

/**
 * Initialize visible columns state from column config.
 */
export function initializeVisibleColumns<TRow>(
  columns: AppTableColumn<TRow>[]
): VisibleColumnsState {
  return columns.reduce((acc, col) => {
    acc[String(col.key)] = col.defaultVisible ?? true;
    return acc;
  }, {} as VisibleColumnsState);
}

/**
 * Get columns that should be rendered based on visibility state.
 */
export function getVisibleColumns<TRow>(
  columns: AppTableColumn<TRow>[],
  visibleColumns: VisibleColumnsState
): AppTableColumn<TRow>[] {
  return columns.filter((col) => {
    const key = String(col.key);
    // If visibility is explicitly set, use it; otherwise default to visible
    return visibleColumns[key] !== false;
  });
}

/**
 * Get hideable columns (for column toggle UI).
 */
export function getHideableColumns<TRow>(
  columns: AppTableColumn<TRow>[]
): AppTableColumn<TRow>[] {
  return columns.filter((col) => col.hideable !== false && col.key !== 'actions');
}

// ─────────────────────────────────────────────────────────────
// Storage Keys
// ─────────────────────────────────────────────────────────────

/**
 * Generate storage key for column visibility preferences.
 * @param routePath - Current route path (e.g., '/programs/:programId/users')
 */
export function getColumnVisibilityStorageKey(routePath: string): string {
  return `appTable:columns:${routePath}`;
}

/**
 * Load column visibility from localStorage.
 */
export function loadColumnVisibility(storageKey: string): VisibleColumnsState | null {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Save column visibility to localStorage.
 */
export function saveColumnVisibility(
  storageKey: string,
  visibleColumns: VisibleColumnsState
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

// ─────────────────────────────────────────────────────────────
// Default Page Size Options
// ─────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
